/**
 * Card Component
 * 
 * A versatile card component with multiple size variants.
 * Uses the tailwind-utils for consistent styling.
 */
import { HTMLAttributes } from 'react'
import { cn, twStyles } from '../../utils/tailwind-utils'

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The size variant of the card which determines padding
   */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Card component with various size variants
 * 
 * @example
 * // Medium card (default)
 * <Card>Card content</Card>
 * 
 * @example
 * // Small card with custom class
 * <Card size="sm" className="max-w-md">Small card</Card>
 */
function Card({
  className,
  size = 'md',
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        twStyles.card.base,
        twStyles.card[size],
        className
      )}
      {...props}
    />
  )
}

/**
 * Card header component
 */
function CardHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col space-y-1.5', className)}
      {...props}
    />
  )
}

/**
 * Card title component
 */
function CardTitle({
  className,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn('text-xl font-semibold', className)}
      {...props}
    />
  )
}

/**
 * Card description component
 */
function CardDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

/**
 * Card content component
 */
function CardContent({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('pt-4', className)}
      {...props}
    />
  )
}

/**
 * Card footer component
 */
function CardFooter({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center pt-4', className)}
      {...props}
    />
  )
}

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter }
export default Card
